import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Users, Maximize } from "lucide-react";
import { api, eur } from "@/lib/api";
import { FadeUp, Overline } from "@/components/motion";
import { useContent } from "@/lib/content";

export default function RoomsSection() {
  const [rooms, setRooms] = useState([]);
  const c = useContent();

  useEffect(() => {
    api.get("/rooms").then((r) => setRooms(r.data)).catch(() => {});
  }, []);

  return (
    <section id="zimmer" className="py-24 lg:py-36 bg-cream" data-testid="rooms-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <FadeUp>
            <Overline>{c.rooms_overline}</Overline>
            <h2 className="mt-4 font-serif font-medium text-3xl sm:text-4xl lg:text-5xl leading-[1.12] tracking-tight text-forest">
              {c.rooms_title_1} <em className="italic text-terra">{c.rooms_title_2}</em>
            </h2>
          </FadeUp>
          <FadeUp delay={0.15}>
            <p className="text-base text-ink/60 max-w-md leading-relaxed">
              {c.rooms_subline}
            </p>
          </FadeUp>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {rooms.map((room, i) => (
            <FadeUp key={room.id} delay={0.08 * i}>
              <article
                className="group bg-white rounded-3xl overflow-hidden border border-forest/10 hover:border-gold/60 hover:shadow-2xl hover:shadow-forest/10 transition-[border-color,box-shadow] duration-500 flex flex-col h-full"
                data-testid={`room-card-${room.id}`}
              >
                <div className="relative h-60 overflow-hidden">
                  <img
                    src={room.image_url}
                    alt={room.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute top-4 left-4 rounded-full bg-sand/90 backdrop-blur px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-forest">
                    {room.category}
                  </span>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-serif text-2xl font-semibold text-forest">{room.name}</h3>
                  <div className="mt-2 flex items-center gap-4 text-xs text-ink/50 font-medium">
                    <span className="flex items-center gap-1"><Users size={13} /> bis {room.capacity} Pers.</span>
                    <span className="flex items-center gap-1"><Maximize size={13} /> {room.size_sqm} m²</span>
                  </div>
                  <p className="mt-3 text-sm text-ink/60 leading-relaxed flex-1">{room.description}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {(room.amenities || []).slice(0, 3).map((a) => (
                      <span key={a} className="rounded-full bg-mist/60 px-3 py-1 text-[11px] font-medium text-forest">
                        {a}
                      </span>
                    ))}
                  </div>
                  <div className="mt-6 pt-5 border-t border-forest/10 flex items-center justify-between">
                    <div>
                      <p className="font-serif text-2xl font-semibold text-forest">ab {eur(room.price_per_night)}</p>
                      <p className="text-[11px] text-ink/45">pro Zimmer und Nacht</p>
                    </div>
                    <Link
                      to={`/buchen?room=${room.id}`}
                      data-testid={`room-book-button-${room.id}`}
                      className="rounded-full bg-forest text-sand px-5 py-2.5 text-sm font-semibold flex items-center gap-1.5 hover:bg-forest-dark transition-colors duration-300"
                    >
                      Buchen <ArrowUpRight size={15} />
                    </Link>
                  </div>
                </div>
              </article>
            </FadeUp>
          ))}
          {rooms.length === 0 && (
            <p className="col-span-full text-center text-ink/50 py-16" data-testid="rooms-empty-note">
              Die Zimmerkategorien werden gerade eingerichtet — schauen Sie gleich wieder vorbei.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
